/**
 * Sponsor registration — pay 900 RP + 100 TP; drill-down tree to pick placement (spillover supported).
 * From 全球玩家(表): locked legacy form (no tree picker).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usersAPI } from '../api/users';
import { formatApiErrorMessage } from '../utils/api-error-message';
import { useAuthStore } from '../store/auth';
import { PlacementTreePicker, type PlacementSelection } from '../components/PlacementTreePicker';
import {
  memberPaymentFormula,
  ReadonlyFormField,
  RegistrationActionButtons,
  RegistrationAgreementRow,
  SponsorBalanceGrid,
  StarPackageSelect,
  type RegisterStarPackage,
} from '../components/global-players/GlobalTableRegistrationForm';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import {
  THEMED_SURFACE_COLORS as C,
  ThemedBottomActionBar,
  ThemedField,
  ThemedInput,
  ThemedNotice,
  ThemedPanel,
  ThemedSubmitButton,
} from '../components/ui/ThemedSurface';

interface RegisterMemberPreview {
  star_package?: RegisterStarPackage;
  e3_enabled?: boolean;
  available_packages?: RegisterStarPackage[];
  sponsor: { username: string; invite_code: string };
  placement: { id: number; username: string };
  required: { rp: string; tp: string; queue_ap?: string; queue_usd?: string };
  balances: { rp: string; tp: string };
  sufficient: boolean;
}

function useLockedPlacement(searchParams: URLSearchParams) {
  return useMemo(() => {
    const fromTable = searchParams.get('from') === 'global-table';
    const id = Number(searchParams.get('placement_user_id') || '');
    const pos = (searchParams.get('position') ?? '').toUpperCase();
    if (!fromTable || !Number.isFinite(id) || id <= 0 || (pos !== 'L' && pos !== 'R')) {
      return null;
    }
    return {
      userId: id,
      position: pos as 'L' | 'R',
      playerNumber: searchParams.get('placement_player')?.trim() || String(id),
      nickname: searchParams.get('placement_nickname')?.trim() || '',
    };
  }, [searchParams]);
}

export function RegisterMember() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();
  const profile = useAuthStore((s) => s.profile) as Record<string, unknown> | null;

  const lockedPlacement = useLockedPlacement(searchParams);
  const rootUserId = profile?.id as number | undefined;
  const rootUsername = (profile?.username as string | undefined) ?? '—';
  const sponsorPlayerNumber = (profile?.player_number as string | undefined)?.trim() || String(rootUserId ?? '—');
  const sponsorNickname = (profile?.nickname as string | undefined)?.trim() || rootUsername;

  const [username, setUsername] = useState('');
  const [nickname, setNickname] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [starPackage, setStarPackage] = useState<RegisterStarPackage>(() => {
    const code = (profile?.level_code as string | undefined) ?? 'E2';
    if (code === 'E1') return 'E1';
    if (code === 'E3') return 'E2'; // E3 may be gated; default to E2 until preview loads
    return 'E2';
  });
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [selection, setSelection] = useState<PlacementSelection | null>(() => {
    if (!lockedPlacement) return null;
    return {
      userId: lockedPlacement.userId,
      username: lockedPlacement.nickname || String(lockedPlacement.userId),
      position: lockedPlacement.position,
    };
  });

  const urlPlacement = useMemo(() => {
    const id = Number(searchParams.get('placement_user_id') || searchParams.get('parent_id') || '');
    const pos = (searchParams.get('position') ?? searchParams.get('zone') ?? '').toUpperCase();
    const usernameHint = searchParams.get('placement_nickname')?.trim() || searchParams.get('placement_username')?.trim();
    return {
      userId: Number.isFinite(id) && id > 0 ? id : undefined,
      position: pos === 'R' ? ('R' as const) : pos === 'L' ? ('L' as const) : undefined,
      usernameHint: usernameHint || undefined,
    };
  }, [searchParams]);

  const previewQuery = useQuery({
    queryKey: ['register-member', 'preview', selection?.userId, starPackage],
    queryFn: async () =>
      (await usersAPI.registerMemberPreview(
        selection ? { placement_user_id: selection.userId, star_package: starPackage } : undefined,
      )) as unknown as RegisterMemberPreview,
    enabled: !!selection,
  });

  const balancesQuery = useQuery({
    queryKey: ['balances', 'register-member', rootUserId],
    queryFn: () => usersAPI.getBalances(),
    enabled: !!rootUserId && !!lockedPlacement,
    staleTime: 30_000,
  });

  const preview = previewQuery.data;
  const balances = balancesQuery.data?.balances ?? preview?.balances;
  const availablePackages: RegisterStarPackage[] = preview?.available_packages?.length
    ? preview.available_packages
    : ['E1', 'E2'];

  useEffect(() => {
    if (!availablePackages.includes(starPackage)) {
      setStarPackage(availablePackages.includes('E2') ? 'E2' : availablePackages[0]!);
    }
  }, [availablePackages, starPackage]);
  const requiredRp = preview?.required.rp ?? '—';
  const requiredTp = preview?.required.tp ?? '—';
  const requiredFc = (preview?.required as { fc?: string } | undefined)?.fc ?? '0.05';
  const balanceRp = preview?.balances.rp ?? balances?.rp ?? '0';
  const balanceTp = preview?.balances.tp ?? balances?.tp ?? '0';
  const balanceFc =
    (preview?.balances as { fc?: string } | undefined)?.fc ??
    (balances as { fc?: string } | undefined)?.fc ??
    '0';

  useEffect(() => {
    if (selection || !rootUserId || lockedPlacement) return;
    if (urlPlacement.userId && urlPlacement.position) {
      setSelection({
        userId: urlPlacement.userId,
        username:
          urlPlacement.usernameHint ??
          (urlPlacement.userId === rootUserId ? rootUsername : String(urlPlacement.userId)),
        position: urlPlacement.position,
      });
    }
  }, [rootUserId, rootUsername, selection, lockedPlacement, urlPlacement.userId, urlPlacement.position, urlPlacement.usernameHint]);

  const register = useMutation({
    mutationFn: () =>
      usersAPI.registerMember({
        username,
        password,
        password_confirm: passwordConfirm,
        position: selection!.position,
        placement_user_id: selection!.userId,
        star_package: starPackage,
      }),
    onSuccess: (data: { username?: string }) => {
      setMsg({
        type: 'ok',
        text: t('registerMember.success', {
          username: data?.username ?? username,
          defaultValue: `Member ${data?.username ?? username} registered successfully`,
        }),
      });
      void qc.invalidateQueries({ queryKey: ['profile'] });
      void qc.invalidateQueries({ queryKey: ['balances'] });
      void qc.invalidateQueries({ queryKey: ['register-member', 'preview'] });
      void qc.invalidateQueries({ queryKey: ['binary-tree'] });
      setTimeout(() => navigate('/trading', { replace: true }), 1800);
    },
    onError: (e: unknown) => {
      setMsg({
        type: 'err',
        text: formatApiErrorMessage(e, t('registerMember.failed', '注册失败')),
      });
    },
  });

  const resetForm = () => {
    setUsername('');
    setNickname('');
    setPassword('');
    setPasswordConfirm('');
    setAgreed(false);
    setMsg(null);
  };

  const handleSubmit = () => {
    setMsg(null);
    if (!agreed && lockedPlacement) {
      setMsg({ type: 'err', text: t('auth.agreeTerms', '请先同意用户协议') });
      return;
    }
    if (!selection) {
      setMsg({ type: 'err', text: t('registerMember.pickPlacement', '请先在网体上选择安置位置') });
      return;
    }
    if (!preview?.sufficient) {
      setMsg({ type: 'err', text: t('registerMember.insufficient', 'RP / TP / FC 余额不足') });
      return;
    }
    if (!username.trim() || username.length < 3) {
      setMsg({ type: 'err', text: '用户名至少 3 个字符' });
      return;
    }
    if (!/^[a-zA-Z0-9_.]+$/.test(username)) {
      setMsg({ type: 'err', text: '用户名仅支持英文字母、数字、下划线和点' });
      return;
    }
    if (password.length < 8 || !/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password)) {
      setMsg({ type: 'err', text: t('auth.passwordHint', '密码须含大小写与数字，至少 8 位') });
      return;
    }
    if (password !== passwordConfirm) {
      setMsg({ type: 'err', text: t('auth.passwordMismatch', '两次输入的密码不一致') });
      return;
    }
    register.mutate();
  };

  if (lockedPlacement) {
    const placementId = lockedPlacement.playerNumber;
    const placementNick = lockedPlacement.nickname;
    const canSubmit =
      !!selection &&
      agreed &&
      !!preview?.sufficient &&
      !register.isPending &&
      username.trim().length >= 3 &&
      password.length >= 8;

    return (
      <div className="relative min-h-full" style={{ background: C.pageBg }}>
        <div className="relative mx-auto min-h-full w-full max-w-[430px]">
          <ThemedPageHeader
            title={t('globalPlayers.registerNewPlayer', '新玩家注册')}
            onBack={() => navigate(-1)}
            theme={C}
          />

          <main className="space-y-4 px-4 pb-8 pt-[70px]">
          <ThemedPanel className="space-y-4 p-4">
            <ThemedField label={t('registerForm.loginName', '玩家登录名')} required>
              <ThemedInput
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoCapitalize="none"
                placeholder={t('registerForm.loginNameHint', '自定义6-15位字母数字组合')}
              />
            </ThemedField>
            <ThemedField label={t('registerForm.nickname', '昵称')} required>
              <ThemedInput
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                autoCapitalize="none"
                placeholder={t('registerForm.nicknameHint', '自定义昵称')}
              />
            </ThemedField>
            <ThemedField label={t('registerForm.password', '玩家登录密码')} required>
              <ThemedInput
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('registerForm.passwordHint', '至少6位，含大小写字母和数字')}
              />
            </ThemedField>
            <ThemedField label={t('registerForm.passwordConfirm', '重复登录密码')} required>
              <ThemedInput
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                placeholder={t('registerForm.passwordConfirmHint', '再次输入密码')}
              />
            </ThemedField>

            <ReadonlyFormField
              label={t('registerForm.referrerId', '推荐人ID')}
              value={sponsorPlayerNumber}
              hint={`${t('registerForm.referrerNickname', '推荐人昵称')}:${sponsorNickname}`}
            />
            <ReadonlyFormField
              label={t('registerForm.placementId', '接点人ID')}
              value={placementId}
              hint={
                placementNick
                  ? `${t('registerForm.placementNickname', '接点人昵称')}:${placementNick}`
                  : undefined
              }
            />

            <SponsorBalanceGrid
              cells={[
                { key: 'rp', label: t('registerForm.rpBalance', 'RP余额'), value: balances?.rp ?? '0', color: '#F5EC63' },
                { key: 'tp', label: t('registerForm.tpBalance', 'TP余额'), value: balances?.tp ?? '0', color: '#F5EC63' },
                { key: 'fc', label: t('registerForm.fcBalance', 'FC余额'), value: (balances as { fc?: string })?.fc ?? '0', color: '#F5EC63' },
                { key: 'mp', label: t('registerForm.mpBalance', 'MP余额'), value: (balances as { mp?: string })?.mp ?? '0', color: '#F5EC63' },
              ]}
            />

            <StarPackageSelect
              label={t('registerForm.starLevel', '玩家星级')}
              value={starPackage}
              onChange={setStarPackage}
              available={availablePackages}
            />
            <ReadonlyFormField
              label={t('registerForm.paymentMethod', '支付方式')}
              value={memberPaymentFormula(starPackage)}
              hint={
                preview?.required
                  ? `需 ${preview.required.rp} RP + ${preview.required.tp} TP + ${(preview.required as { fc?: string }).fc ?? '0.05'} FC`
                  : undefined
              }
            />

            <RegistrationAgreementRow agreed={agreed} onAgreedChange={setAgreed} />
          </ThemedPanel>

          {msg ? <ThemedNotice tone={msg.type === 'ok' ? 'success' : 'error'}>{msg.text}</ThemedNotice> : null}

          <RegistrationActionButtons
            onCancel={() => navigate(-1)}
            onReset={resetForm}
            onSubmit={handleSubmit}
            submitLabel={t('common.submit', '提交')}
            submitting={register.isPending}
            submitDisabled={!canSubmit}
          />
        </main>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
      <ThemedPageHeader title={t('registerMember.title', 'Register New Member')} onBack={() => navigate(-1)} theme={C} />

      <main className="space-y-4 px-4 pb-28 pt-[70px]">
        <ThemedPanel className="p-4">
          <p className="text-xs leading-relaxed" style={{ color: C.muted }}>
            {t(
              'registerMember.subtitle',
              '选择一星/二星（及后台开启的三星）配套注册新会员：扣 RP/TP → 购买队列 + AP → 静/动奖励。点击左/右区选择安置（含滑落）。',
            )}
          </p>
          <div className="mt-3">
            <StarPackageSelect
              label={t('registerForm.starLevel', '玩家星级')}
              value={starPackage}
              onChange={setStarPackage}
              available={availablePackages}
            />
          </div>
          {preview?.sponsor && (
            <div className="mt-3 rounded-xl border px-3 py-2 text-xs" style={{ borderColor: C.line, color: C.text }}>
              {t('registerMember.sponsor', 'Sponsor')}: <span className="font-semibold">{preview.sponsor.username}</span>
            </div>
          )}
        </ThemedPanel>

        <ThemedPanel className="p-4">
          <div className="mb-3 text-sm font-semibold" style={{ color: C.text }}>
            {t('registerMember.treeTitle', '选择旗下安置位置')}
          </div>
          {rootUserId ? (
            <PlacementTreePicker
              rootUserId={rootUserId}
              rootUsername={rootUsername}
              selection={selection}
              onSelect={setSelection}
              initialUserId={urlPlacement.userId}
              initialPosition={urlPlacement.position}
            />
          ) : (
            <div className="text-xs" style={{ color: C.muted }}>
              {t('common.loading', '加载中…')}
            </div>
          )}
        </ThemedPanel>

        <ThemedPanel className="p-4">
          <div className="mb-3 text-sm font-semibold" style={{ color: C.text }}>
            {t('registerMember.costTitle', 'Registration cost')}
          </div>
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div className="rounded-xl border px-3 py-2" style={{ borderColor: C.line }}>
              <div style={{ color: C.muted }}>RP</div>
              <div className="mt-1 font-mono text-base font-semibold" style={{ color: C.gold }}>
                {requiredRp}
              </div>
              <div className="mt-1" style={{ color: C.muted }}>
                {t('registerMember.balance', 'Balance')}: {Number(balanceRp).toFixed(2)}
              </div>
            </div>
            <div className="rounded-xl border px-3 py-2" style={{ borderColor: C.line }}>
              <div style={{ color: C.muted }}>TP</div>
              <div className="mt-1 font-mono text-base font-semibold" style={{ color: C.gold }}>
                {requiredTp}
              </div>
              <div className="mt-1" style={{ color: C.muted }}>
                {t('registerMember.balance', 'Balance')}: {Number(balanceTp).toFixed(2)}
              </div>
            </div>
            <div className="rounded-xl border px-3 py-2" style={{ borderColor: C.line }}>
              <div style={{ color: C.muted }}>FC</div>
              <div className="mt-1 font-mono text-base font-semibold" style={{ color: C.gold }}>
                {requiredFc}
              </div>
              <div className="mt-1" style={{ color: C.muted }}>
                {t('registerMember.balance', 'Balance')}: {Number(balanceFc).toFixed(2)}
              </div>
            </div>
          </div>
          {preview && !preview.sufficient && (
            <ThemedNotice tone="error">
              {t('registerMember.insufficient', 'RP / TP / FC 余额不足')}
            </ThemedNotice>
          )}
          {preview?.required.queue_ap && (
            <div className="mt-3 rounded-xl border px-3 py-2 text-xs leading-relaxed" style={{ borderColor: C.line, color: C.muted }}>
              {t('registerMember.queueDesc', {
                ap: preview.required.queue_ap,
                usd: preview.required.queue_usd ?? '—',
                defaultValue: `注册成功后进入购买队列，排队 AP ${preview.required.queue_ap}（${preview.required.queue_usd ?? '—'}$ 配套）。`,
              })}
            </div>
          )}
        </ThemedPanel>

        <ThemedPanel className="space-y-4 p-4">
          <ThemedField label={t('auth.username', 'Username')} required>
            <ThemedInput value={username} onChange={(e) => setUsername(e.target.value)} autoCapitalize="none" />
          </ThemedField>

          <ThemedField label={t('auth.password', 'Password')} required>
            <ThemedInput type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
            <p className="mt-1 text-[10px] leading-relaxed" style={{ color: C.muted }}>
              {t('auth.passwordHint', '密码须含大小写与数字，至少 8 位')}
            </p>
          </ThemedField>
          <ThemedField label={t('auth.confirmPassword', 'Confirm Password')} required>
            <ThemedInput type="password" value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} />
          </ThemedField>
        </ThemedPanel>

        {msg && (
          <ThemedNotice tone={msg.type === 'ok' ? 'success' : 'error'}>{msg.text}</ThemedNotice>
        )}
      </main>

      <ThemedBottomActionBar>
        <ThemedSubmitButton
          onClick={handleSubmit}
          disabled={
            !selection ||
            !preview?.sufficient ||
            register.isPending
          }
        >
          {register.isPending
            ? t('auth.loggingIn', 'Processing…')
            : `支付 ${requiredRp} RP + ${requiredTp} TP + ${requiredFc} FC 并注册`}
        </ThemedSubmitButton>
      </ThemedBottomActionBar>
      </div>
    </div>
  );
}
